import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Shield,
  Trash2,
  Users,
  MoreHorizontal,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  User,
  Mail,
  Calendar,
  AlertTriangle,
} from "lucide-react";
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  SearchFilterBar,
  FilterSettings,
} from "@/components/ui/search-filter-bar";
import { apiService } from "@/lib/api-service";
import { useAuth } from "@/lib/auth-context";
import { UserRole } from "@employee-management/common";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { SectionHeading } from "@/components/ui/section-heading";

interface ProjectAccessUser {
  id: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  createdAt: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface ProjectAccessManagementProps {
  projectId: string;
  isProjectCreator?: boolean;
}

export function ProjectAccessManagement({
  projectId,
  isProjectCreator = false,
}: ProjectAccessManagementProps) {
  const { hasRole } = useAuth();
  const [addAccessOpen, setAddAccessOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [filterSettings, setFilterSettings] = useState<FilterSettings>({
    searchTerm: "",
    filters: {},
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: "user.name", desc: false },
  ]);
  const queryClient = useQueryClient();

  // Show this component to managers, superadmins, or project creators
  const canManageAccess = hasRole(UserRole.MANAGER) || isProjectCreator;

  if (!canManageAccess) {
    return null;
  }

  const { data: accessUsers, isLoading: accessLoading } = useQuery({
    queryKey: ["project-access", projectId],
    queryFn: async () => {
      const res = await apiService.get<ProjectAccessUser[]>(
        `/projects/${projectId}/access`
      );
      return res;
    },
  });

  const { data: availableUsers } = useQuery({
    queryKey: ["access-users", projectId],
    queryFn: async () => {
      // Use the project-specific endpoint for project creators
      const endpoint = isProjectCreator
        ? `/projects/${projectId}/access/users`
        : "/projects/access/users";
      const res = await apiService.get<User[]>(endpoint);
      return res;
    },
  });

  // Filter data based on filterSettings
  const filteredData = useMemo(() => {
    if (!accessUsers) return [];
    if (
      !filterSettings.searchTerm &&
      Object.keys(filterSettings.filters).length === 0
    ) {
      return accessUsers;
    }

    let filtered = [...accessUsers];

    // Apply search term
    if (filterSettings.searchTerm.trim()) {
      const searchLower = filterSettings.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.user.name.toLowerCase().includes(searchLower) ||
          item.user.email.toLowerCase().includes(searchLower) ||
          item.user.role.toLowerCase().includes(searchLower)
      );
    }

    // Apply filters
    Object.entries(filterSettings.filters).forEach(([key, value]) => {
      if (value && value !== "all") {
        filtered = filtered.filter((item) => {
          if (key === "role") {
            return item.user.role === value;
          }
          return true;
        });
      }
    });

    return filtered;
  }, [accessUsers, filterSettings]);

  const addAccessMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiService.post(`/projects/${projectId}/access`, { userId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["project-access", projectId],
      });
      setAddAccessOpen(false);
      setSelectedUserId("");
      toast.success("Access granted successfully");
    },
    onError: () => {
      toast.error("Failed to grant access");
    },
  });

  const removeAccessMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiService.delete(`/projects/${projectId}/access/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["project-access", projectId],
      });
      toast.success("Access removed successfully");
    },
    onError: () => {
      toast.error("Failed to remove access");
    },
  });

  const handleAddAccess = () => {
    if (selectedUserId) {
      addAccessMutation.mutate(selectedUserId);
    }
  };

  const handleRemoveAccess = (userId: string) => {
    removeAccessMutation.mutate(userId);
  };

  // Filter out users who already have access
  const availableUsersForAccess =
    availableUsers?.filter(
      (user) => !accessUsers?.some((access) => access.user.id === user.id)
    ) || [];

  const columns: ColumnDef<ProjectAccessUser>[] = [
    {
      accessorKey: "user.name",
      header: ({ column }) => (
        <Button
          type="button"
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
        >
          <User className="h-4 w-4" />
          Name
          {column.getIsSorted() === "asc" ? (
            <ArrowUp className="h-4 w-4" />
          ) : column.getIsSorted() === "desc" ? (
            <ArrowDown className="h-4 w-4" />
          ) : (
            <ArrowUpDown className="h-4 w-4" />
          )}
        </Button>
      ),
      cell: ({ row }) => (
        <div className="font-medium flex items-center gap-3">
          <div className="flex items-center justify-center p-3 bg-muted rounded-lg">
            <User className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex flex-col gap-1">
            <p>{row.original.user.name}</p>
            <p className="text-xs text-muted-foreground">
              {row.original.user.email}
            </p>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "user.email",
      header: ({ column }) => (
        <Button
          type="button"
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
        >
          <Mail className="h-4 w-4" />
          Email
          {column.getIsSorted() === "asc" ? (
            <ArrowUp className="h-4 w-4" />
          ) : column.getIsSorted() === "desc" ? (
            <ArrowDown className="h-4 w-4" />
          ) : (
            <ArrowUpDown className="h-4 w-4" />
          )}
        </Button>
      ),
      cell: ({ row }) => (
        <div className="text-sm">{row.original.user.email}</div>
      ),
    },
    {
      accessorKey: "user.role",
      header: ({ column }) => (
        <Button
          type="button"
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
        >
          <Shield className="h-4 w-4" />
          Role
          {column.getIsSorted() === "asc" ? (
            <ArrowUp className="h-4 w-4" />
          ) : column.getIsSorted() === "desc" ? (
            <ArrowDown className="h-4 w-4" />
          ) : (
            <ArrowUpDown className="h-4 w-4" />
          )}
        </Button>
      ),
      cell: ({ row }) => (
        <Badge variant="secondary">{row.original.user.role}</Badge>
      ),
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => (
        <Button
          type="button"
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
        >
          <Calendar className="h-4 w-4" />
          Access Granted
          {column.getIsSorted() === "asc" ? (
            <ArrowUp className="h-4 w-4" />
          ) : column.getIsSorted() === "desc" ? (
            <ArrowDown className="h-4 w-4" />
          ) : (
            <ArrowUpDown className="h-4 w-4" />
          )}
        </Button>
      ),
      cell: ({ row }) => (
        <div className="text-sm text-muted-foreground">
          {new Date(row.original.createdAt).toLocaleDateString()}
        </div>
      ),
    },
    {
      id: "actions",
      header: " ",
      cell: ({ row }) => {
        const access = row.original;
        return (
          <div className="text-right">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => handleRemoveAccess(access.user.id)}
                  className="text-red-600 focus:text-red-600"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remove Access
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const filterOptions = [
    {
      key: "role",
      label: "Role",
      type: "select" as const,
      options: [
        { value: "supervisor", label: "Supervisor" },
        { value: "manager", label: "Manager" },
        { value: "superadmin", label: "Super Admin" },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <SectionHeading
        title={
          <div className="flex items-center space-x-2">
            <Shield className="w-8 h-8 text-orange-600" />
            <span>Access Management</span>
          </div>
        }
        subtitle={
          isProjectCreator
            ? "Manage access for other supervisors to this project."
            : "Manage project access permissions and security settings."
        }
        actions={
          <Dialog open={addAccessOpen} onOpenChange={setAddAccessOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4" />
                Grant Access
              </Button>
            </DialogTrigger>
          </Dialog>
        }
        size="lg"
      />

      <Dialog open={addAccessOpen} onOpenChange={setAddAccessOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Grant Project Access</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Alert variant="warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Warning</AlertTitle>
              <AlertDescription>
                You only need to grant access to supervisors if you want them to
                be able to view & manage the project. Managers and superadmins
                can always view & manage all projects.
              </AlertDescription>
            </Alert>
            <div>
              <label className="text-sm font-medium">Select Supervisor</label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a supervisor" />
                </SelectTrigger>
                <SelectContent>
                  {availableUsersForAccess.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setAddAccessOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAddAccess}
                disabled={!selectedUserId || addAccessMutation.isPending}
              >
                {addAccessMutation.isPending ? "Granting..." : "Grant Access"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <SearchFilterBar
        filterOptions={filterOptions}
        placeholder="Search supervisors..."
        onFilterChange={setFilterSettings}
      />

      {accessLoading ? (
        <div className="text-center py-4">Loading...</div>
      ) : filteredData && filteredData.length > 0 ? (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="!bg-muted/40">
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className="hover:bg-transparent"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No supervisors found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>
            No supervisors have been granted explicit access to this project.
          </p>
          <p className="text-sm">
            {isProjectCreator
              ? "Only you and managers have access to this project."
              : "Only the project creator and managers have access."}
          </p>
        </div>
      )}
    </div>
  );
}
